import { Button } from './ui/button';
import { ArrowLeft } from 'lucide-react';
import { PathkitLogo } from './PathkitLogo';

export function PrivacyPolicy() {
  const lastUpdated = "May 9, 2026";

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <PathkitLogo />
              <span className="text-xl">Pathkit</span>
            </div>
            <Button variant="ghost" onClick={() => window.location.href = '/'}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-lg border border-gray-200 p-8 md:p-12">
          <h1 className="text-4xl mb-4">Privacy Policy</h1>
          <p className="text-gray-600 mb-8">Last Updated: {lastUpdated}</p>

          <div className="prose prose-gray max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl mb-4">1. Introduction</h2>
              <p className="text-gray-700 mb-4">
                Welcome to Pathkit ("we," "our," or "us"). We are committed to protecting your privacy and personal data. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use 
                our consulting platform and project scoping tool.
              </p>
              <p className="text-gray-700">
                By using Pathkit, you agree to the collection and use of information in accordance with this policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl mb-4">2. Information We Collect</h2>
              
              <h3 className="text-xl mb-3 mt-6">2.1 Information You Provide</h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li><strong>Account Information:</strong> Name, email address, company name, and job title</li>
                <li><strong>Profile Data:</strong> Professional information, preferences, and settings</li>
                <li><strong>Project Data:</strong> Service desk configurations, templates, and documentation you create</li>
                <li><strong>Communication Data:</strong> Messages, feedback, and support inquiries</li>
                <li><strong>Payment Information:</strong> Billing details processed securely through our payment processor</li>
              </ul>

              <h3 className="text-xl mb-3 mt-6">2.2 Automatically Collected Information</h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li><strong>Usage Data:</strong> How you interact with our platform, features used, and time spent</li>
                <li><strong>Device Information:</strong> Browser type, operating system, IP address, and device identifiers</li>
                <li><strong>Log Data:</strong> Server logs, error reports, and performance data</li>
                <li><strong>Cookies:</strong> Session cookies, preference cookies, and analytics cookies</li>
              </ul>

              <h3 className="text-xl mb-3 mt-6">2.3 Atlassian Integration Data</h3>
              <p className="text-gray-700 mb-4">
                When you connect Pathkit with Atlassian products (Jira, Confluence), we access:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Project configurations and settings</li>
                <li>Custom fields and workflows</li>
                <li>User information (names, roles)</li>
                <li>Service desk request types</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl mb-4">3. How We Use Your Information</h2>
              <p className="text-gray-700 mb-4">We use your information to:</p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Provide, operate, and maintain our platform</li>
                <li>Process your transactions and manage your subscriptions</li>
                <li>Send you technical notices, updates, and security alerts</li>
                <li>Provide customer support and respond to your inquiries</li>
                <li>Analyze usage patterns to improve our services</li>
                <li>Detect, prevent, and address technical issues and fraud</li>
                <li>Send marketing communications (with your consent)</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl mb-4">4. Data Sharing and Disclosure</h2>
              
              <h3 className="text-xl mb-3 mt-6">4.1 We Share Your Information With:</h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li><strong>Service Providers:</strong> Hosting (Supabase), payment processing (Stripe), email services (Resend), AI processing (OpenAI)</li>
                <li><strong>Atlassian:</strong> When you use our Forge integration to connect with Jira/Confluence</li>
                <li><strong>Your Organization:</strong> Data within your consulting firm or client companies you manage</li>
                <li><strong>Legal Authorities:</strong> When required by law or to protect our rights</li>
              </ul>

              <h3 className="text-xl mb-3 mt-6">4.2 We Do NOT:</h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Sell your personal information to third parties</li>
                <li>Share your data with advertisers</li>
                <li>Use your project configurations for any purpose other than providing our service</li>
                <li>Use your data to train AI models</li>
              </ul>

              <h3 className="text-xl mb-3 mt-6">4.3 AI Processing</h3>
              <p className="text-gray-700 mb-4">
                AI-assisted features (suggestions, transcription, extraction) are powered by OpenAI, called
                server-side from PathKit. OpenAI's default API policy prohibits training on the data we
                send and limits retention to up to 30 days for abuse monitoring, after which the data is
                deleted. For the full list of what we send, what we don't do, and the controls available
                to you, see the dedicated{' '}
                <a href="/ai-trust" className="text-blue-600 hover:underline">PathKit AI Data Use</a>{' '}
                page.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl mb-4">5. Data Security</h2>
              <p className="text-gray-700 mb-4">We implement industry-standard security measures:</p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Encryption in transit (TLS/SSL) and at rest</li>
                <li>Multi-tenant data isolation</li>
                <li>Role-based access control (RBAC)</li>
                <li>Regular security audits and penetration testing</li>
                <li>Secure authentication with OAuth 2.0</li>
                <li>Automated backups and disaster recovery</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl mb-4">6. Data Retention</h2>
              <p className="text-gray-700 mb-4">
                We retain your information for as long as your account is active or as needed to provide services. 
                After account deletion:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Most data is deleted within 30 days</li>
                <li>Some data may be retained for legal or compliance purposes</li>
                <li>Backup copies are purged within 90 days</li>
                <li>Anonymized analytics data may be retained indefinitely</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl mb-4">7. Your Rights</h2>
              <p className="text-gray-700 mb-4">Depending on your location, you have rights to:</p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal data</li>
                <li><strong>Portability:</strong> Export your data in a machine-readable format</li>
                <li><strong>Objection:</strong> Object to certain processing activities</li>
                <li><strong>Withdrawal:</strong> Withdraw consent for marketing communications</li>
              </ul>
              <p className="text-gray-700">
                To exercise these rights, contact us at privacy@pathkit.pro
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl mb-4">8. International Data Transfers</h2>
              <p className="text-gray-700 mb-4">
                Your data may be transferred to and processed in countries other than your own. We ensure appropriate 
                safeguards are in place, including:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Standard contractual clauses approved by the European Commission</li>
                <li>Data processing agreements with all service providers</li>
                <li>Compliance with GDPR for EU data subjects</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl mb-4">9. Children's Privacy</h2>
              <p className="text-gray-700">
                Pathkit is not intended for individuals under 18. We do not knowingly collect personal information from 
                children. If you believe a child has provided us with personal data, please contact us immediately.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl mb-4">10. Changes to This Policy</h2>
              <p className="text-gray-700">
                We may update this Privacy Policy periodically. We will notify you of significant changes by email or 
                through our platform. Your continued use of Pathkit after changes constitutes acceptance of the updated policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl mb-4">11. Contact Us</h2>
              <p className="text-gray-700 mb-4">
                If you have questions about this Privacy Policy or our data practices:
              </p>
              <ul className="list-none mb-4 text-gray-700 space-y-2">
                <li><strong>Email:</strong> privacy@pathkit.pro</li>
                <li><strong>Support:</strong> support@pathkit.pro</li>
                <li><strong>Address:</strong> Pathkit Privacy Team</li>
              </ul>
            </section>

            <section className="mb-8" id="cookies">
              <h2 className="text-2xl mb-4">12. Cookie Policy</h2>
              
              <h3 className="text-xl mb-3 mt-6">What Are Cookies?</h3>
              <p className="text-gray-700 mb-4">
                Cookies are small text files stored on your device when you visit our website. They help us 
                provide you with a better experience by remembering your preferences and enabling certain features.
              </p>

              <h3 className="text-xl mb-3 mt-6">Cookies We Use</h3>
              
              <div className="space-y-4 mb-4">
                <div className="border-l-4 border-blue-500 pl-4 py-2">
                  <h4 className="font-semibold text-gray-900">Necessary Cookies (Always Active)</h4>
                  <p className="text-sm text-gray-700 mt-1">
                    Required for the website to function properly. Cannot be disabled.
                  </p>
                  <ul className="list-disc pl-6 text-sm text-gray-700 mt-2 space-y-1">
                    <li><code>pathkit_access_token</code> - Authentication session token</li>
                    <li><code>pathkit_cookie_consent</code> - Stores your cookie preferences (1 year)</li>
                    <li><code>sidebar_state</code> - UI preference for sidebar (7 days)</li>
                  </ul>
                </div>

                <div className="border-l-4 border-green-500 pl-4 py-2">
                  <h4 className="font-semibold text-gray-900">Functional Cookies (Optional)</h4>
                  <p className="text-sm text-gray-700 mt-1">
                    Remember your preferences and settings.
                  </p>
                  <ul className="list-disc pl-6 text-sm text-gray-700 mt-2 space-y-1">
                    <li>Language preferences</li>
                    <li>Theme settings (light/dark mode)</li>
                    <li>Layout customizations</li>
                  </ul>
                </div>

                <div className="border-l-4 border-purple-500 pl-4 py-2">
                  <h4 className="font-semibold text-gray-900">Analytics Cookies (Optional)</h4>
                  <p className="text-sm text-gray-700 mt-1">
                    Help us understand how visitors use our website. All data is anonymized.
                  </p>
                  <p className="text-sm text-gray-700 mt-2 italic">
                    We currently do not use analytics cookies. If implemented in the future, we will update 
                    this policy and seek your consent.
                  </p>
                </div>

                <div className="border-l-4 border-orange-500 pl-4 py-2">
                  <h4 className="font-semibold text-gray-900">Marketing Cookies (Optional)</h4>
                  <p className="text-sm text-gray-700 mt-1">
                    Used to track visitors across websites to display relevant advertisements.
                  </p>
                  <p className="text-sm text-gray-700 mt-2 italic">
                    We do not use marketing or advertising cookies.
                  </p>
                </div>
              </div>

              <h3 className="text-xl mb-3 mt-6">Managing Cookies</h3>
              <p className="text-gray-700 mb-4">
                You can control and manage cookies in several ways:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Use our cookie consent banner when you first visit the site</li>
                <li>Change your preferences at any time by clearing your browser cookies and revisiting</li>
                <li>Configure your browser to block or delete cookies (may affect functionality)</li>
                <li>Use browser privacy modes for temporary sessions</li>
              </ul>

              <h3 className="text-xl mb-3 mt-6">Third-Party Cookies</h3>
              <p className="text-gray-700">
                We do not allow third-party advertising cookies. However, our service providers (Supabase for hosting, 
                Stripe for payments) may set their own cookies. These are covered by their respective privacy policies.
              </p>
            </section>

            <section className="mb-8" id="gdpr">
              <h2 className="text-2xl mb-4">13. GDPR Compliance</h2>
              <p className="text-gray-700 mb-4">
                Pathkit is committed to full compliance with the EU General Data Protection Regulation (GDPR). 
                We have implemented comprehensive measures to protect your rights and ensure lawful processing.
              </p>
              
              <h3 className="text-xl mb-3 mt-6">Your GDPR Rights</h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li><strong>Right of Access:</strong> Request a copy of all your personal data</li>
                <li><strong>Right to Rectification:</strong> Correct inaccurate or incomplete data</li>
                <li><strong>Right to Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
                <li><strong>Right to Restriction:</strong> Limit how we process your data</li>
                <li><strong>Right to Data Portability:</strong> Receive your data in a machine-readable format</li>
                <li><strong>Right to Object:</strong> Object to processing based on legitimate interests</li>
                <li>
                  <strong>Rights Related to Automated Processing:</strong> Opt out of automated decision-making
                  {' '}(see our{' '}
                  <a href="/ai-trust" className="text-blue-600 hover:underline">AI Data Use</a>{' '}
                  page for what automated processing PathKit performs)
                </li>
              </ul>

              <h3 className="text-xl mb-3 mt-6">Exercising Your Rights</h3>
              <p className="text-gray-700 mb-4">
                To exercise any of your GDPR rights, contact us:
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-gray-900"><strong>Email:</strong> privacy@pathkit.pro</p>
                <p className="text-gray-900"><strong>Subject Line:</strong> GDPR Request - [Your Right: Access/Deletion/etc.]</p>
                <p className="text-gray-900"><strong>Response Time:</strong> Within 30 days (may extend to 90 days for complex requests)</p>
              </div>

              <h3 className="text-xl mb-3 mt-6">Legal Basis for Processing</h3>
              <p className="text-gray-700 mb-4">We process your personal data under the following legal bases:</p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li><strong>Contract:</strong> To provide the Pathkit service per our Terms of Service</li>
                <li><strong>Consent:</strong> For optional features like marketing communications</li>
                <li><strong>Legitimate Interest:</strong> For fraud prevention, security, and service improvement</li>
                <li><strong>Legal Obligation:</strong> To comply with applicable laws (e.g., tax records)</li>
              </ul>

              <h3 className="text-xl mb-3 mt-6">Supervisory Authority</h3>
              <p className="text-gray-700 mb-4">
                If you believe we have not adequately addressed your concerns, you have the right to lodge a complaint 
                with your local data protection authority.
              </p>
              <p className="text-gray-700">
                For EU data subjects, find your supervisory authority at: 
                <a href="https://edpb.europa.eu/about-edpb/board/members_en" className="text-blue-600 hover:underline ml-1" target="_blank" rel="noopener noreferrer">
                  https://edpb.europa.eu/about-edpb/board/members_en
                </a>
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}